import React, { useRef, useState } from "react";
import { Upload, Image } from "antd";
import { Icon } from '@iconify/react';
import toast from "@comps/toast";
import { isAcceptedFile } from "@utils/mediaFormats";
import "./inputs.css";

const getBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

// A drag only counts when it carries files — dragging a link or a text
// selection across the page must not light the field up.
const carriesFiles = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');

let dropSeq = 0;

// A dropped File in the shape antd's list expects of a picked one.
const toListEntry = (file) => {
  dropSeq += 1;
  return {
    uid: `drop-${Date.now()}-${dropSeq}`,
    name: file.name,
    size: file.size,
    type: file.type,
    lastModified: file.lastModified,
    percent: 0,
    originFileObj: file,
  };
};

/**
 * File picker.
 *
 * The bordered box with a "Select File" button inside became a real
 * dropzone — the whole area is the target, and it says what it accepts.
 * Behaviour (beforeUpload:false, onUpload callback, existing-file
 * preview) is unchanged.
 *
 * Pass `extensions` (a lowercase list, no dots) alongside `accept` to
 * turn the file dialog's suggestion into a rule. `accept` only filters
 * the dialog, so anything that lands here is checked by name and
 * rejected with a toast.
 *
 * Drops are taken here rather than left to antd. Its dragger ran a drop
 * through `accept` first and threw away whatever did not match without
 * a word, so a wrong file dragged in simply vanished; and only the
 * dashed box was a target, which shrinks to a single tile once files
 * are picked. The whole field — the list of picked files included —
 * takes a drop now, and it goes through the same check as a pick.
 */
const BaseUploadFile = (props) => {
  // `extensions` and `formatLabel` are ours — antd's Upload would pass
  // them straight through to the DOM node.
  const { extensions, formatLabel, hideBorder, uploadText, ...rest } = props;
  const [fileList, setFileList] = useState([]);
  const [existFile, setExistFile] = useState(props.defaultValue ? props.defaultValue : []);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [dragging, setDragging] = useState(false);
  // dragenter/dragleave fire for every child the pointer crosses, so the
  // highlight follows a count rather than the last event seen.
  const dragDepth = useRef(0);

  /*
   * The list is driven from state (`fileList` is handed back to Upload
   * below), which is what makes a rejection stick. It used to be state
   * that nothing read: antd kept its own internal list, so a file this
   * rejected was still drawn as a row, and every later change
   * re-validated it and fired the same toast again — which is why
   * uploading a perfectly good STL after one bad drop reported the STL
   * as unsupported. Rejected files never enter the list now, so there is
   * nothing left to re-reject.
   */
  const onChange = ({ fileList: incoming }) => {
    const accepted = filterAccepted(incoming);
    // Only one file is wanted here, so a second pick replaces the first
    // rather than quietly stacking up behind it.
    const newFileList = props.multiple ? accepted : accepted.slice(-1);

    setFileList(newFileList);

    if (newFileList.length > 0) {
      props.onUpload(props.multiple ? newFileList : newFileList[0].originFileObj);
    } else {
      props.onUpload(null);
    }
  };

  /**
   * Drop anything the section does not accept and say so once, naming the
   * files rather than silently swallowing them.
   */
  const filterAccepted = (files) => {
    if (!extensions || !extensions.length) return files;

    const rejected = files.filter((file) => !isAcceptedFile(file.name, extensions));
    if (rejected.length === 0) return files;

    toast.error(
      rejected.length === 1 ? "That file type isn't accepted" : "Some files weren't accepted",
      `${rejected.map((file) => file.name).join(', ')} — this field takes ${formatLabel || extensions.join(', ').toUpperCase()} files.`
    );

    return files.filter((file) => isAcceptedFile(file.name, extensions));
  };

  /*
   * Every handler below stops the event on its way down: antd's dragger
   * listens for the same three, and letting a drop reach it would add
   * the files a second time.
   */
  const onDragEnter = (e) => {
    if (!carriesFiles(e)) return;
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current += 1;
    if (!props.disabled) setDragging(true);
  };

  const onDragOver = (e) => {
    if (!carriesFiles(e)) return;
    // Without this the browser refuses the drop and opens the file.
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = props.disabled ? 'none' : 'copy';
  };

  const onDragLeave = (e) => {
    if (!carriesFiles(e)) return;
    e.stopPropagation();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  };

  const onDrop = (e) => {
    if (!carriesFiles(e)) return;
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current = 0;
    setDragging(false);
    if (props.disabled) return;

    const dropped = Array.from(e.dataTransfer.files || []).map(toListEntry);
    if (dropped.length === 0) return;

    onChange({ fileList: props.multiple ? [...fileList, ...dropped] : dropped });
  };

  const onRemove = () => {
    setFileList([]);
    setExistFile([]);
  };

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  const showDropHint = fileList.length === 0 && existFile.length === 0
    && !props.multiple && !props.listType;

  return (
    <div className="upload-field">
      { props.label && <label className="upload-label">{ props.label }</label> }

      <div
        className={ `dropzone ${hideBorder ? 'is-bare' : ''} ${dragging ? 'is-dragover' : ''}` }
        onDragEnterCapture={ onDragEnter }
        onDragOverCapture={ onDragOver }
        onDragLeaveCapture={ onDragLeave }
        onDropCapture={ onDrop }
      >
        <Upload.Dragger
          beforeUpload={ () => false }
          onPreview={ handlePreview }
          onChange={ onChange }
          listType={ props.listType ? props.listType : "picture" }
          { ...rest }
          // After `rest` on purpose: the list is ours to control, and a
          // caller passing its own would put antd back in charge of what
          // a rejected file does.
          fileList={ fileList }
          multiple={ props.multiple ? props.multiple : false }
          className="dropzone-inner"
        >
          { showDropHint
            ? <div className="dropzone-hint">
              <span className="dropzone-mark">
                <Icon icon="tabler:cloud-upload" width={ 22 } />
              </span>
              <span className="dropzone-text">
                <strong>{ props.text || "Choose a file" }</strong>
                <span>or drag it here</span>
              </span>
            </div>
            : <span className="dropzone-compact">
              <span>{ uploadText }</span>
              <span className="dropzone-formats">or drag { props.multiple ? 'files' : 'a file' } here</span>
              { formatLabel && <span className="dropzone-formats">{ formatLabel }</span> }
            </span> }
        </Upload.Dragger>

        { previewImage && (
          <Image
            wrapperStyle={ { display: 'none' } }
            preview={ {
              visible: previewOpen,
              onVisibleChange: (visible) => setPreviewOpen(visible),
              afterOpenChange: (visible) => !visible && setPreviewImage(''),
            } }
            src={ previewImage }
          />
        ) }
      </div>

      { existFile.length > 0 &&
        <div className="upload-existing">
          <img src={ process.env.REACT_APP_BASE_URL + existFile[0].url } alt="Current file" />
          { !props.disabled &&
            <button type="button" className="upload-existing-remove" onClick={ onRemove } aria-label="Remove file">
              <Icon icon="tabler:x" width={ 16 } />
            </button> }
        </div> }
    </div>
  );
};

export default BaseUploadFile;
